<template>
    <div class="tree-multi-selector" ref="root">
        <input type="hidden" :name="inputName" :value="selectedIdsString">

        <!-- Champ cliquable -->
        <div class="form-control d-flex align-items-center justify-content-between cursor-pointer"
             style="min-height: 38px; cursor: pointer;"
             @click="toggleDropdown">
            <span v-if="selectedItems.length > 0" class="text-dark" style="line-height: 1.4; word-break: break-word;">
                {{ selectedItems.map(i => i.name).join(', ') }}
            </span>
            <span v-else class="text-muted">
                {{ placeholder }}
            </span>
            <div class="d-flex align-items-center">
                <button v-if="selectedItems.length > 0" type="button" class="btn btn-sm p-0 text-danger mr-1" @click.stop="clearAll" title="Tout effacer">
                    <i class="fal fa-times-circle"></i>
                </button>
                <i class="far fa-chevron-down text-muted"></i>
            </div>
        </div>

        <!-- Dropdown arborescent -->
        <div v-if="dropdownOpen" class="tree-multi-dropdown">
            <!-- Barre de recherche -->
            <div class="p-2 border-bottom">
                <input type="text"
                       class="form-control form-control-sm"
                       placeholder="Rechercher..."
                       v-model="searchQuery"
                       ref="searchInput">
            </div>

            <!-- Arbre -->
            <div class="tree-multi-list">
                <div v-if="loading" class="text-center py-3">
                    <i class="far fa-circle-notch fa-spin text-muted"></i>
                </div>
                <div v-else-if="filteredFlatTree.length === 0" class="text-center py-3 text-muted small">
                    Aucun résultat trouvé.
                </div>
                <div v-else>
                    <div v-for="node in filteredFlatTree"
                         :key="node.id"
                         class="tree-multi-node"
                         :class="{ 'selected': isSelected(node.id) }"
                         @click="toggleSelect(node)">
                        <span v-for="d in node.depth" :key="'g-' + d" class="tree-indent"></span>
                        <span class="tree-chevron" v-if="node.children && node.children.length > 0" @click.stop="toggleNode(node.id)">
                            <i class="far fa-chevron-right" :style="{ transform: isExpanded(node.id) ? 'rotate(90deg)' : 'none' }"></i>
                        </span>
                        <span class="tree-chevron" v-else></span>
                        <span class="tree-checkbox">
                            <i v-if="isSelected(node.id)" class="far fa-check-square text-primary"></i>
                            <i v-else class="far fa-square text-muted"></i>
                        </span>
                        <span class="tree-label" :class="'tree-depth-' + Math.min(node.depth, 2)">{{ node.name }}</span>
                    </div>
                </div>
            </div>

            <!-- Pied avec compteur -->
            <div class="tree-multi-footer border-top p-2 d-flex justify-content-between align-items-center">
                <small class="text-muted">
                    {{ selectedItems.length }} sélectionné{{ selectedItems.length > 1 ? 's' : '' }}
                </small>
                <button v-if="selectedItems.length > 0" type="button" class="btn btn-sm btn-link text-danger p-0" @click="clearAll">
                    Tout effacer
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'tree-multi-selector',
    props: {
        inputName: {
            type: String,
            required: true
        },
        apiUrl: {
            type: String,
            required: true
        },
        dataKey: {
            type: String,
            default: 'families'
        },
        placeholder: {
            type: String,
            default: 'Sélectionner...'
        },
        initialIds: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            items: [],
            selectedIds: new Set(),
            loading: false,
            dropdownOpen: false,
            searchQuery: '',
            expandedNodeIds: new Set()
        };
    },
    computed: {
        selectedIdsString() {
            return Array.from(this.selectedIds).join(';');
        },
        selectedItems() {
            return this.flatTree.filter(n => this.selectedIds.has(n.id));
        },
        flatTree() {
            const map = {};
            this.items.forEach(item => {
                map[item.id] = { ...item, children: [] };
            });

            const roots = [];
            this.items.forEach(item => {
                const node = map[item.id];
                if (node.parent_id && map[node.parent_id]) {
                    map[node.parent_id].children.push(node);
                } else {
                    roots.push(node);
                }
            });

            const flat = [];
            const traverse = (node, depth, path) => {
                node.depth = depth;
                node.path = path;
                flat.push(node);
                if (node.children && node.children.length > 0) {
                    node.children.forEach(child => {
                        traverse(child, depth + 1, [...path, node.id]);
                    });
                }
            };

            roots.forEach(root => {
                traverse(root, 0, []);
            });

            return flat;
        },
        filteredFlatTree() {
            const query = this.searchQuery.trim().toLowerCase();

            if (!query) {
                return this.flatTree.filter(node => {
                    return node.path.every(parentId => this.expandedNodeIds.has(parentId));
                });
            }

            const matchingNodeIds = new Set();
            this.flatTree.forEach(node => {
                if (node.name.toLowerCase().includes(query)) {
                    matchingNodeIds.add(node.id);
                }
            });

            const nodesToShow = new Set();
            this.flatTree.forEach(node => {
                const hasMatchingAncestor = node.path.some(parentId => matchingNodeIds.has(parentId));
                const isAncestorOfMatch = this.flatTree.some(other =>
                    matchingNodeIds.has(other.id) && other.path.includes(node.id)
                );

                if (matchingNodeIds.has(node.id) || hasMatchingAncestor || isAncestorOfMatch) {
                    nodesToShow.add(node.id);
                }
            });

            return this.flatTree.filter(node => nodesToShow.has(node.id));
        }
    },
    methods: {
        loadItems() {
            this.loading = true;
            fetch(this.apiUrl, {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(response => {
                if (!response.ok) throw new Error("Erreur de chargement");
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    this.items = data[this.dataKey] || [];
                    this.expandFirstLevel();
                    this.restoreSelection();
                }
                this.loading = false;
            })
            .catch(error => {
                console.error("Erreur de chargement", error);
                this.loading = false;
            });
        },
        expandFirstLevel() {
            this.items.forEach(item => {
                if (!item.parent_id) {
                    this.expandedNodeIds.add(item.id);
                }
            });
        },
        restoreSelection() {
            if (this.initialIds) {
                this.initialIds.split(';').forEach(id => {
                    if (id) this.selectedIds.add(parseInt(id));
                });
            }
        },
        isExpanded(id) {
            return this.expandedNodeIds.has(id);
        },
        toggleNode(id) {
            if (this.expandedNodeIds.has(id)) {
                this.expandedNodeIds.delete(id);
            } else {
                this.expandedNodeIds.add(id);
            }
            this.expandedNodeIds = new Set(this.expandedNodeIds);
        },
        toggleDropdown() {
            this.dropdownOpen = !this.dropdownOpen;
            if (this.dropdownOpen && this.items.length === 0) {
                this.loadItems();
            }
            if (this.dropdownOpen) {
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
            }
        },
        isSelected(id) {
            return this.selectedIds.has(id);
        },
        toggleSelect(node) {
            if (this.selectedIds.has(node.id)) {
                this.selectedIds.delete(node.id);
            } else {
                this.selectedIds.add(node.id);
            }
            this.selectedIds = new Set(this.selectedIds);
        },
        clearAll() {
            this.selectedIds = new Set();
        },
        handleClickOutside(e) {
            if (this.$refs.root && !this.$refs.root.contains(e.target)) {
                this.dropdownOpen = false;
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    }
};
</script>

<style scoped>
.tree-multi-selector {
    position: relative;
}

.cursor-pointer {
    cursor: pointer;
}

.tree-multi-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 9999;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-top: 2px;
}

.tree-multi-list {
    max-height: 280px;
    overflow-y: auto;
    padding: 4px 0;
}

.tree-multi-node {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 13px;
    color: #334155;
}

.tree-multi-node:hover {
    background-color: #f1f5f9;
}

.tree-multi-node.selected {
    background-color: #dbeafe;
}

.tree-indent {
    display: inline-block;
    width: 20px;
    flex: 0 0 20px;
}

.tree-chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    flex: 0 0 20px;
    color: #94a3b8;
    font-size: 11px;
    cursor: pointer;
}

.tree-checkbox {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    flex: 0 0 20px;
    margin-right: 4px;
    font-size: 14px;
}

.tree-label.tree-depth-0 {
    font-weight: 700;
    color: #1e293b;
}

.tree-label.tree-depth-1 {
    font-weight: 600;
}

.tree-multi-footer {
    background-color: #f8fafc;
    border-radius: 0 0 8px 8px;
}
</style>
